import { inject, Injectable, signal, Signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, Observable } from 'rxjs';
import { PortalRouteData, RouteItem } from '../../../routes/app.types';
import { Breadcrumb } from './breadcrumbs.types';

@Injectable()
export class BreadcrumbsService {
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  readonly #isLastSegmentDynamic = signal<boolean>(false);
  public readonly isLastSegmentDynamic: Signal<boolean> = this.#isLastSegmentDynamic.asReadonly();
  public readonly breadcrumbs$: Observable<Breadcrumb[]> = this.router.events.pipe(
    filter(e => e instanceof NavigationEnd),
    map(() => this.buildBreadcrumbs(this.route.root))
  );

  private buildBreadcrumbs(route: ActivatedRoute, url = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
    //If no routeConfig is avalailable we are on the root path
    const breadcrumbableRoute: RouteItem | null =
      route.routeConfig && route.routeConfig.data ? (route.routeConfig.data as PortalRouteData).routeItem : null;
    const path = route.routeConfig && route.routeConfig.data && route.routeConfig.path ? route.routeConfig.path : '';
    //In the routeConfig the complete path is not available, so we rebuild it each time
    const nextUrl = path ? `${url}/${path}` : url;
    const createNewBreadcrumbs = (): Breadcrumb[] => {
      // If the route is a dynamic route, e.g. `PortalPath.metering + '/:id'`, we need to create a second breadcrumb for the dynamic route part `':id'`.
      const lastRoutePart = path.split('/').pop() || '';
      this.#isLastSegmentDynamic.set(lastRoutePart.startsWith(':'));
      if (this.#isLastSegmentDynamic() && !!route.snapshot) {
        const dynamicParamName = lastRoutePart.split(':')[1];
        const dynamicParamValue = route.snapshot.params[dynamicParamName];
        return [
          this.createBreadcrumb(breadcrumbableRoute!.title, nextUrl.replace(lastRoutePart, '')),
          this.createBreadcrumb(
            dynamicParamValue,
            path ? `${url}/${path.replace(lastRoutePart, dynamicParamValue)}` : url
          )
        ];
      } else {
        return [this.createBreadcrumb(breadcrumbableRoute!.title, nextUrl, breadcrumbableRoute!.isNonNavigable)];
      }
    };

    const newBreadcrumbs = breadcrumbableRoute ? [...breadcrumbs, ...createNewBreadcrumbs()] : [...breadcrumbs];
    if (route.firstChild) {
      //If we are not on our current path yet,
      //there will be more children to look after, to build our breadcumb
      return this.buildBreadcrumbs(route.firstChild, nextUrl, newBreadcrumbs);
    }
    return newBreadcrumbs;
  }

  private createBreadcrumb(title: string, url: string, isNonNavigable = false): Breadcrumb {
    return {
      title,
      url,
      isNonNavigable
    };
  }
}
